// src/config/permissions/types.ts

// Importar y re-exportar UserRole desde models para conveniencia
import type { UserRole } from '../../types/models';
export type { UserRole } from '../../types/models';

/**
 * Acciones posibles sobre un recurso
 */
export type ResourceAction = 
  | 'access'  // Puede ver el recurso en el menú
  | 'list'    // Puede ver la lista
  | 'show'    // Puede ver detalles
  | 'create'  // Puede crear nuevos
  | 'edit'    // Puede editar existentes
  | 'delete'  // Puede eliminar
  | 'export'  // Puede exportar datos
  | 'import'; // Puede importar datos

/**
 * Recursos disponibles en el sistema
 */
export type ResourceName = 
  | 'groups'
  | 'users'
  | 'hotels'
  | 'zones'
  | 'lockers'
  | 'locker-types'
  | 'locker-controllers'
  | 'hmis'
  | 'assignments'
  | 'tags'
  | 'tag-types'
  | 'reports'
  | 'settings'
  | 'dashboard';

/**
 * Definición de permisos para un recurso
 */
export interface ResourcePermissions {
  [action: string]: UserRole[];
}

/**
 * Configuración completa de un recurso
 */
export interface ResourceConfig {
  name: ResourceName;
  permissions: ResourcePermissions;
  customPermissions?: {
    [key: string]: UserRole[];
  };
}

/**
 * Mapa de todos los recursos
 */
export type ResourcesMap = {
  [K in ResourceName]: ResourceConfig;
};

/**
 * Resultado del hook de permisos base
 */
export interface BasePermissions {
  role: UserRole | null;
  canAccess: (resource: ResourceName) => boolean;
  canList: (resource: ResourceName) => boolean;
  canShow: (resource: ResourceName) => boolean;
  canCreate: (resource: ResourceName) => boolean;
  canEdit: (resource: ResourceName) => boolean;
  canDelete: (resource: ResourceName) => boolean;
  canExport: (resource: ResourceName) => boolean;
  canImport: (resource: ResourceName) => boolean;
  canPerform: (resource: ResourceName, action: ResourceAction) => boolean;
  hasRole: (...roles: UserRole[]) => boolean;
  isAtLeastRole: (minRole: UserRole) => boolean;
}

/**
 * Permisos extendidos para un recurso específico
 */
export interface ResourceSpecificPermissions extends BasePermissions {
  // Permisos básicos con sintaxis simplificada
  canView: boolean;
  canAdd: boolean;
  canModify: boolean;
  canRemove: boolean;
  
  // Permisos específicos del recurso
  [key: string]: any;
}

/**
 * Niveles de roles (para comparación jerárquica)
 */
export const ROLE_LEVELS: Record<UserRole, number> = {
  SuperAdmin: 0,
  GroupAdmin: 1,
  HotelAdmin: 2,
  Receptionist: 3,
  User: 4,
};

/**
 * Tipo para reglas de permisos condicionales
 */
export interface ConditionalPermission {
  roles: UserRole[];
  condition?: (context: PermissionContext) => boolean;
}

/**
 * Contexto para evaluar permisos condicionales
 */
export interface PermissionContext {
  user: {
    id: string;
    role: UserRole;
    hotelId?: string;
    groupId?: string;
  };
  resource?: {
    id?: string;
    ownerId?: string;
    hotelId?: string;
    groupId?: string;
    [key: string]: any;
  };
}

/**
 * Configuración de permisos con soporte para condiciones
 */
export interface AdvancedResourceConfig extends ResourceConfig {
  conditionalPermissions?: {
    [action: string]: ConditionalPermission[];
  };
}

/**
 * Helper type para extraer acciones de un recurso
 */
export type ResourceActions<T extends ResourceName> = 
  keyof ResourcesMap[T]['permissions'];

/**
 * Helper type para permisos tipados por recurso
 */
export type TypedResourcePermissions<T extends ResourceName> = {
  [K in ResourceActions<T> as `can${Capitalize<string & K>}`]: boolean;
};